'use client';

import Image from '@/components/Image';
import Input from '@/components/ui/Input';
import { staticFilters } from '@/constants';
import { PathEnum } from '@/i18n/locale-config';
import { usePathname, useRouter } from '@/i18n/navigation';
import { useLocale, useTranslations } from 'next-intl';
import Filters from './Filters';

const screeningTypesLabels = {
    online: 'virtualExclusive',
    offline: 'inCinema',
};

interface FiltrationProps {
    resultCount: number;
    searchParams: {
        focuses?: string;
        categories?: string;
        screeningType?: 'offline' | 'online';
        interests?: string;
        countries?: string;
        year?: string;
    };
    filters: {
        categories: { id: string; label: string }[];
        screeningTypes: { id: string; label: string }[];
        focuses: { id: string; label: string }[];
        interests: { id: string; label: string }[];
        countries: { id: string; label: string }[];
        years: { id: string; label: string }[];
    };
    isFiltersApplied: boolean;
}

const Filtration = (props: FiltrationProps) => {
    const {
        resultCount,
        isFiltersApplied,
        searchParams,
        filters: { categories, screeningTypes, focuses, interests, countries, years },
    } = props;
    const t = useTranslations();
    const locale = useLocale() as 'en' | 'fr';
    const router = useRouter();
    const pathname = usePathname();

    const getLabelsByIds = (
        ids: string[] | undefined,
        filters: { id: string; label: string }[],
    ) => {
        if (!ids) return [];
        return filters.filter((filter) => ids.includes(filter.id)).map((filter) => filter.label);
    };

    const selectedFocuses = searchParams.focuses ? searchParams.focuses.split(',') : undefined;
    const selectedCategories = searchParams.categories
        ? searchParams.categories.split(',')
        : undefined;
    const selectedInterest = searchParams.interests ? searchParams.interests.split(',') : undefined;
    const selectedCountry = searchParams.countries ? searchParams.countries.split(',') : undefined;
    const selectedYear = searchParams.year ? [searchParams.year] : undefined;

    const appliedStaticFilters = staticFilters('filters').filter(
        (filter) => selectedFocuses && selectedFocuses.includes(filter.id),
    );
    const filteredSelectedFocuses = selectedFocuses?.filter(
        (f: string) => !appliedStaticFilters.some((sf) => sf.id === f),
    );
    const selectedFocusLabels = getLabelsByIds(filteredSelectedFocuses, focuses);
    const selectedCategoryLabels = getLabelsByIds(selectedCategories, categories);
    const selectedInterestLabels = getLabelsByIds(selectedInterest, interests);
    const selectedCountryLabels = getLabelsByIds(selectedCountry, countries);
    const selectedYearLabels = getLabelsByIds(selectedYear, years);

    const clearAllFilters = () => {
        // Clear all filters by resetting the search parameters
        const params = new URLSearchParams();
        // @ts-ignore
        router.push(`${PathEnum[pathname]}?${params.toString()}`);
    };

    const clearFilter = (filterKey: string) => {
        // Clear a specific filter
        const params = new URLSearchParams(searchParams as Record<string, string>);
        params.delete(filterKey);
        // @ts-ignore
        router.push(`${PathEnum[pathname]}?${params.toString()}`);
    };

    const handleSearch = (value: string) => {
        const params = new URLSearchParams(searchParams as Record<string, string>);
        params.set('search', value);
        // @ts-ignore
        router.push(`${PathEnum[pathname]}?${params.toString()}`);
    };

    return (
        <>
            {/* Filters */}
            <div className="flex justify-between max-lg:flex-col-reverse flex-wrap gap-4 max-lg:pb-3">
                <Filters
                    data={[
                        {
                            label: t('Filters.filmType'),
                            filters: categories,
                            key: 'categories',
                            selectedFilters: selectedCategories,
                        },
                        {
                            label: t('Filters.screeningType'),
                            filters: screeningTypes,
                            key: 'screeningType',
                            selectedFilters: searchParams.screeningType
                                ? [searchParams.screeningType]
                                : undefined,
                        },
                        {
                            label: 'Focus',
                            filters: focuses,
                            key: 'focuses',
                            selectedFilters: selectedFocuses,
                        },
                        {
                            label: t('Filters.interest'),
                            filters: interests,
                            key: 'interests',
                            selectedFilters: selectedInterest,
                        },
                        {
                            label: t('Filters.country'),
                            filters: countries,
                            key: 'countries',
                            selectedFilters: selectedCountry,
                        },
                        {
                            label: t('Filters.year'),
                            filters: years,
                            key: 'year',
                            selectedFilters: selectedYear,
                        },
                    ]}
                    searchParams={searchParams}
                />
                <Input
                    className="newsletter-input"
                    type="text"
                    name="search"
                    id="search"
                    placeholder={t('Common.search')}
                    autoComplete="false"
                    color="white"
                    search
                    // @ts-ignore
                    defaultValue={searchParams.search ?? ''}
                    onChange={(e) => handleSearch(e.target.value)}
                />
            </div>

            {/* SELECTED FILTERS */}
            {isFiltersApplied && (
                <div className="flex gap-3 flex-wrap items-center mt-8 max-lg:hidden">
                    <span className="font-helvetica-neue font-light text-gray-200 text-md">
                        {t('FilmPage.results', {
                            count: resultCount,
                        })}
                    </span>
                    <button
                        onClick={clearAllFilters}
                        className="py-0.5 px-3 text-primary flex items-center border border-primary uppercase text-[25px] leading-8"
                    >
                        {t('Common.clearAll')}
                    </button>
                    {appliedStaticFilters.map((filter) => (
                        <button
                            key={filter.id}
                            onClick={() => clearFilter('focuses')}
                            className="h-9 py-0.5 px-3 flex items-center border border-disabled uppercase text-[25px] leading-8"
                        >
                            <Image
                                src={filter.icon}
                                alt={filter.name[locale]}
                                width={36}
                                height={36}
                                className="w-auto h-7"
                            />
                            <Image
                                src="/img/icons/close-pink.svg"
                                alt="Close"
                                width={36}
                                height={36}
                                className="w-6 h-6 ml-5"
                            />
                        </button>
                    ))}
                    {searchParams.screeningType && (
                        <button
                            onClick={() => clearFilter('screeningType')}
                            className="h-9 py-0.5 px-3 flex items-center border border-disabled uppercase text-[25px] leading-8"
                        >
                            {t(
                                `Cart.${
                                    screeningTypesLabels[
                                        searchParams.screeningType.toLowerCase() as
                                            | 'online'
                                            | 'offline'
                                    ]
                                }`,
                            )}
                            <Image
                                src="/img/icons/close-pink.svg"
                                alt="Close"
                                width={36}
                                height={36}
                                className="w-6 h-6 ml-5"
                            />
                        </button>
                    )}
                    {selectedFocusLabels.map((label) => (
                        <button
                            key={label}
                            onClick={() => clearFilter('focuses')}
                            className="h-9 py-0.5 px-3 flex items-center border border-disabled uppercase text-[25px] leading-8"
                        >
                            {label}
                            <Image
                                src="/img/icons/close-pink.svg"
                                alt="Close"
                                width={36}
                                height={36}
                                className="w-6 h-6 ml-5"
                            />
                        </button>
                    ))}
                    {selectedCategoryLabels.map((label) => (
                        <button
                            key={label}
                            onClick={() => clearFilter('categories')}
                            className="h-9 py-0.5 px-3 flex items-center border border-disabled uppercase text-[25px] leading-8"
                        >
                            {label}
                            <Image
                                src="/img/icons/close-pink.svg"
                                alt="Close"
                                width={36}
                                height={36}
                                className="w-6 h-6 ml-5"
                            />
                        </button>
                    ))}
                    {selectedInterestLabels.map((label) => (
                        <button
                            key={label}
                            onClick={() => clearFilter('interests')}
                            className="h-9 py-0.5 px-3 flex items-center border border-disabled uppercase text-[25px] leading-8"
                        >
                            {label}
                            <Image
                                src="/img/icons/close-pink.svg"
                                alt="Close"
                                width={36}
                                height={36}
                                className="w-6 h-6 ml-5"
                            />
                        </button>
                    ))}
                    {selectedCountryLabels.map((label) => (
                        <button
                            key={label}
                            onClick={() => clearFilter('countries')}
                            className="h-9 py-0.5 px-3 flex items-center border border-disabled uppercase text-[25px] leading-8"
                        >
                            {label}
                            <Image
                                src="/img/icons/close-pink.svg"
                                alt="Close"
                                width={36}
                                height={36}
                                className="w-6 h-6 ml-5"
                            />
                        </button>
                    ))}
                    {selectedYearLabels.map((label) => (
                        <button
                            key={label}
                            onClick={() => clearFilter('year')}
                            className="h-9 py-0.5 px-3 flex items-center border border-disabled uppercase text-[25px] leading-8"
                        >
                            {label}
                            <Image
                                src="/img/icons/close-pink.svg"
                                alt="Close"
                                width={36}
                                height={36}
                                className="w-6 h-6 ml-5"
                            />
                        </button>
                    ))}
                </div>
            )}
        </>
    );
};

export default Filtration;
